import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, CheckCircle2, XCircle, ChevronRight, RefreshCw } from "lucide-react";
import { useDailyQuestion } from "@/hooks/useDailyQuestion";
import { renderMarkdownWithMath } from "@/lib/markdown-math";
import { Confetti } from "@/components/dashboard/Confetti";
import { cn } from "@/lib/cn";

export type DailyQuestionCardProps = {
  userId: string | null;
  classId: string | null;
  studentName: string | null;
};

/**
 * Daily-question widget. Hybrid behaviour:
 *   - One deterministic question per calendar day, locked once answered.
 *   - After answering, the card unlocks a "Try another?" practice mode
 *     that pulls random questions from the same pool.
 *
 * Pool priority: completed chapters -> touched chapters -> demo chapters
 * in the user's class. Brand-new users see a discovery question with a
 * gentler hook line.
 *
 * Visuals: small confetti burst on correct answers (reward moment).
 */
export function DailyQuestionCard({ userId, classId, studentName }: DailyQuestionCardProps) {
  const state = useDailyQuestion({ userId, classId, studentName });

  // Local UI state -- which option did the user pick this round.
  const [pickedIdx, setPickedIdx] = useState<number | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [confetti, setConfetti] = useState(0);

  // Sync the "already taken today" state into the UI on first paint.
  useMemo(() => {
    if (state.kind === "ready" && state.alreadyTakenIndex != null && pickedIdx === null) {
      setPickedIdx(state.alreadyTakenIndex);
      setRevealed(true);
    }
    // We intentionally don't include pickedIdx -- this should only run once
    // per state.kind transition, not bounce back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.kind]);

  if (state.kind === "loading") return <CardSkeleton />;
  if (state.kind === "empty") return null;

  const { question, hookLine } = state;

  const handlePick = (i: number) => {
    if (revealed) return;
    setPickedIdx(i);
    setRevealed(true);
    state.markTaken(i);
    if (i === question.correctAnswer) {
      setConfetti((c) => c + 1);
    }
  };

  const handleAnother = () => {
    setPickedIdx(null);
    setRevealed(false);
    state.pickRandom();
  };

  const isCorrect = revealed && pickedIdx === question.correctAnswer;

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
      className={cn(
        "relative rounded-2xl border border-border bg-surface p-5 sm:p-6",
        // Subtle brand tint when answered correctly -- earns the moment.
        isCorrect ? "border-success/40 shadow-[0_4px_24px_rgb(34_197_94_/_0.08)]" : "",
      )}
    >
      {/* Confetti origin: top-centre of the card so the particles arc
          over the question. Wrapped in a relative positioning context. */}
      <Confetti
        trigger={confetti}
        count={22}
        spread={130}
        origin={{ x: "50%", y: "0%" }}
        colors={["#22C55E", "#F97316", "#FB923C", "#FED7AA"]}
      />

      <header className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-brand">
            <Sparkles className="h-3.5 w-3.5" />
            <span className="text-[10px] uppercase tracking-[0.16em] font-semibold">
              {state.kind === "ready" && question.source === "discovery"
                ? "Try a sample"
                : "Daily check-in"}
            </span>
          </div>
          <h3 className="mt-1 font-display text-base sm:text-lg font-semibold tracking-tight">
            {hookLine}
          </h3>
          <p className="mt-1 text-xs text-text-muted">
            From{" "}
            <Link
              to={`/study/${encodeURIComponent(question.chapterId)}`}
              className="text-text hover:text-brand transition-colors underline-offset-2 hover:underline"
            >
              {question.chapterName}
            </Link>
          </p>
        </div>
      </header>

      <div
        className="mt-4 text-[15px] sm:text-base leading-relaxed text-text [&_p]:my-0.5 [&_.katex-display]:my-1"
        dangerouslySetInnerHTML={{ __html: renderMarkdownWithMath(question.question) }}
      />

      <ol className="mt-4 space-y-2">
        {question.options.map((opt, i) => {
          const isPick = pickedIdx === i;
          const isAnswer = i === question.correctAnswer;
          const showCorrect = revealed && isAnswer;
          const showWrong = revealed && isPick && !isAnswer;
          return (
            <li key={i}>
              <button
                type="button"
                onClick={() => handlePick(i)}
                disabled={revealed}
                className={cn(
                  "w-full text-left rounded-xl border px-3.5 py-2.5 sm:py-3",
                  "flex items-start gap-3 transition-colors duration-150",
                  !revealed && "border-border hover:bg-surface-2",
                  !revealed && isPick && "border-brand bg-brand-soft",
                  showCorrect && "border-success/60 bg-success/10",
                  showWrong && "border-danger/60 bg-danger/10",
                  revealed && !showCorrect && !showWrong && "border-border opacity-70",
                  "disabled:cursor-default",
                )}
              >
                <span
                  className={cn(
                    "grid place-items-center h-6 w-6 rounded-md text-[11px] font-semibold flex-shrink-0 mt-0.5",
                    showCorrect ? "bg-success text-white"
                      : showWrong ? "bg-danger text-white"
                      : isPick ? "bg-brand text-white"
                      : "bg-surface-2 text-text-muted",
                  )}
                >
                  {String.fromCharCode(65 + i)}
                </span>
                <span
                  className="flex-1 text-[14.5px] [&_p]:my-0"
                  dangerouslySetInnerHTML={{ __html: renderMarkdownWithMath(opt) }}
                />
                {showCorrect ? <CheckCircle2 className="h-4 w-4 text-success flex-shrink-0" /> : null}
                {showWrong   ? <XCircle      className="h-4 w-4 text-danger  flex-shrink-0" /> : null}
              </button>
            </li>
          );
        })}
      </ol>

      <AnimatePresence>
        {revealed ? (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="mt-4 pt-4 border-t border-border"
          >
            {isCorrect ? (
              <p className="text-sm text-success font-medium">
                Nice. {state.kind === "ready" && question.source === "completed"
                  ? "Still got it."
                  : "Now you've got it."}
              </p>
            ) : (
              <p className="text-sm text-text-muted">
                Correct answer was <span className="font-medium text-text">
                  {String.fromCharCode(65 + question.correctAnswer)}
                </span>. {question.explanation ? "Here's why:" : ""}
              </p>
            )}
            {!isCorrect && question.explanation ? (
              <div
                className="mt-2 text-sm text-text-muted [&_p]:my-0.5"
                dangerouslySetInnerHTML={{ __html: renderMarkdownWithMath(question.explanation) }}
              />
            ) : null}

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <Link
                to={`/study/${encodeURIComponent(question.chapterId)}/quiz`}
                className="inline-flex items-center gap-1 text-sm text-brand font-medium hover:underline"
              >
                Open the full quiz
                <ChevronRight className="h-3.5 w-3.5" />
              </Link>
              <button
                type="button"
                onClick={handleAnother}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-text-muted hover:text-text hover:bg-surface-2 transition-colors"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Try another
              </button>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </motion.section>
  );
}

function CardSkeleton() {
  return (
    <div className="rounded-2xl border border-border bg-surface p-5 sm:p-6 animate-pulse">
      <div className="h-3 w-20 rounded bg-surface-2" />
      <div className="mt-3 h-5 w-3/4 rounded bg-surface-2" />
      <div className="mt-2 h-3 w-1/2 rounded bg-surface-2" />
      <div className="mt-5 space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-10 rounded-xl bg-surface-2" />
        ))}
      </div>
    </div>
  );
}
